import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { shiftsAPI, statusAPI } from '@/lib/api';

const MONTH_NAMES = {
  en: ['January','February','March','April','May','June','July','August','September','October','November','December'],
  ru: ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'],
  et: ['Jaanuar','Veebruar','Märts','Aprill','Mai','Juuni','Juuli','August','September','Oktoober','November','Detsember']
};

const DAY_LABELS = {
  en: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'],
  ru: ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'],
  et: ['E','T','K','N','R','L','P']
};

export default function MySchedule() {
  const { t, language } = useLanguage();
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
  const [shifts, setShifts] = useState([]);
  const [objects, setObjects] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);

  const lang = ['en', 'ru', 'et'].includes(language) ? language : 'en';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [sRes, oRes] = await Promise.all([
          shiftsAPI.my(currentMonth, currentYear),
          statusAPI.myObjects().catch(() => ({ data: { objects: [] } }))
        ]);
        setShifts(sRes.data?.shifts || []);
        setObjects(oRes.data?.objects || []);
      } catch (e) {
        console.error('MySchedule load error:', e);
      }
      setLoading(false);
    };
    load();
  }, [currentMonth, currentYear]);

  const prevMonth = () => {
    if (currentMonth === 1) { setCurrentMonth(12); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (currentMonth === 12) { setCurrentMonth(1); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
    setSelectedDay(null);
  };

  // Build calendar cells (Monday-first)
  const firstDow = (new Date(currentYear, currentMonth - 1, 1).getDay() + 6) % 7; // 0=Mon
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  // Pad to full rows
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr = today.toISOString().slice(0, 10);
  const getDateStr = d => `${currentYear}-${String(currentMonth).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  const getDayShifts = d => shifts.filter(s => s.date === getDateStr(d));

  const monthLabel = (MONTH_NAMES[lang] || MONTH_NAMES.en)[currentMonth - 1];
  const dayLabels = DAY_LABELS[lang] || DAY_LABELS.en;

  const selectedShifts = selectedDay ? shifts.filter(s => s.date === selectedDay) : [];
  const selectedDate = selectedDay ? new Date(selectedDay + 'T00:00:00') : null;

  const getLocaleDateStr = (date) => {
    const locales = { en: 'en-US', ru: 'ru-RU', et: 'et-EE' };
    return date.toLocaleDateString(locales[lang] || 'en-US', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className="min-h-screen bg-wp-dark p-4 sm:p-6">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/dashboard" className="text-white/40 hover:text-white transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-xl font-semibold text-white">{t('nav.mySchedule')}</h1>
        </div>

        <div className="glass-card rounded-2xl p-5">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-5">
            <button
              onClick={prevMonth}
              className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-all"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="font-semibold text-white text-base">
              {monthLabel} {currentYear}
            </span>
            <button
              onClick={nextMonth}
              className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-all"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-2">
            {dayLabels.map(d => (
              <div key={d} className="text-center text-xs font-semibold text-white/30 py-1">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          {loading ? (
            <div className="h-48 flex items-center justify-center text-white/30 text-sm">...</div>
          ) : (
            <div className="grid grid-cols-7 gap-y-1">
              {cells.map((day, i) => {
                if (!day) return <div key={i} />;
                const dateStr = getDateStr(day);
                const dayShifts = getDayShifts(day);
                const isToday = dateStr === todayStr;
                const isSelected = dateStr === selectedDay;
                const hasShift = dayShifts.length > 0;
                const isWeekend = ((i % 7) >= 5); // Sat/Sun columns
                return (
                  <div key={i} className="flex justify-center">
                    <button
                      onClick={() => setSelectedDay(dateStr === selectedDay ? null : dateStr)}
                      className={`relative w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                        isSelected ? 'ring-2 ring-wp-light ring-offset-1 ring-offset-wp-darker' : ''
                      } ${
                        hasShift
                          ? 'bg-wp-medium text-white hover:bg-wp-light'
                          : isToday
                            ? 'bg-white/20 text-white'
                            : isWeekend
                              ? 'text-white/20 hover:bg-white/5'
                              : 'text-white/50 hover:bg-white/10'
                      } ${isToday ? 'ring-2 ring-white/40' : ''}`}
                    >
                      {day}
                      {dayShifts.length > 1 && (
                        <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-amber-400 text-black text-[9px] flex items-center justify-center font-bold">
                          {dayShifts.length}
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Selected day card */}
          {selectedDay && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-sm font-medium text-white/70 mb-3">
                {selectedDate && getLocaleDateStr(selectedDate)}
              </p>
              {selectedShifts.length > 0 ? (
                selectedShifts.map((sh, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 py-2 ${i > 0 ? 'border-t border-white/5' : ''}`}
                  >
                    <div className="w-1 h-8 rounded-full bg-wp-light" />
                    <div>
                      <p className="font-semibold text-white text-sm">{sh.start_time} – {sh.end_time}</p>
                      {sh.object_name && (
                        <p className="text-xs text-white/40 mt-0.5">{sh.object_name}</p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-white/30">{t('schedule.dayOff')}</p>
              )}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex gap-4 mt-4 text-xs text-white/30">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-wp-medium inline-block" />
            {t('common.work') || 'Work day'}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-white/10 inline-block" />
            {t('schedule.dayOff') || 'Day off'}
          </span>
        </div>
      </div>
    </div>
  );
}
